import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Spin
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  ReloadOutlined,
  DollarOutlined
} from '@ant-design/icons';
import api from '../utils/axios';
import dayjs from 'dayjs';
import MonthlyLaborCostEstimator from '../components/MonthlyLaborCostEstimator';

// 安全的数组操作辅助函数
const safeArrayFilter = (array, filterFn) => {
  if (!Array.isArray(array)) return [];
  try {
    return array.filter(filterFn);
  } catch (error) {
    console.error('数组过滤操作失败:', error);
    return [];
  }
};

const safeArrayLength = (array) => {
  if (!Array.isArray(array)) return 0;
  return array.length;
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    leads: [],
    interviews: [],
    employees: [],
    onboarding: [],
    followUps: [],
    users: []
  });

  const [metrics, setMetrics] = useState({
    totalLeads: 0,
    totalInterviews: 0,
    totalEmployees: 0,
    conversionRate: 0,
    interviewPassRate: 0,
    retentionRate: 0,
    turnoverRate: 0
  });

  const [todayMetrics, setTodayMetrics] = useState({
    todayLeads: 0,
    todayInterviews: 0,
    todayFollowUps: 0,
    todayOnboarding: 0,
    pendingTasks: 0
  });

  // 成本预估器模态框状态
  const [costEstimatorVisible, setCostEstimatorVisible] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [leadsRes, interviewsRes, employeesRes, onboardingRes, followUpsRes, usersRes] = await Promise.all([
        api.get('/leads'),
        api.get('/interviews'),
        api.get('/employees'),
        api.get('/onboarding'),
        api.get('/follow-ups'),
        api.get('/users')
      ]);

      // 处理新的分页响应格式，确保所有数据都是数组
      const leads = leadsRes.data && leadsRes.data.data ? leadsRes.data.data : (Array.isArray(leadsRes.data) ? leadsRes.data : []);
      const interviews = interviewsRes.data && interviewsRes.data.data ? interviewsRes.data.data : (Array.isArray(interviewsRes.data) ? interviewsRes.data : []);
      const employees = employeesRes.data && employeesRes.data.data ? employeesRes.data.data : (Array.isArray(employeesRes.data) ? employeesRes.data : []);
      const onboarding = onboardingRes.data && onboardingRes.data.data ? onboardingRes.data.data : (Array.isArray(onboardingRes.data) ? onboardingRes.data : []);
      const followUps = Array.isArray(followUpsRes.data) ? followUpsRes.data : [];
      const users = usersRes.data && usersRes.data.data ? usersRes.data.data : (Array.isArray(usersRes.data) ? usersRes.data : []);
      
      console.log('Dashboard数据检查:', {
        leads: Array.isArray(leads) ? leads.length : '非数组',
        interviews: Array.isArray(interviews) ? interviews.length : '非数组',
        employees: Array.isArray(employees) ? employees.length : '非数组',
        onboarding: Array.isArray(onboarding) ? onboarding.length : '非数组',
        followUps: Array.isArray(followUps) ? followUps.length : '非数组'
      });

      setData({ leads, interviews, employees, onboarding, followUps, users });

      // 计算关键指标
      const totalLeads = safeArrayLength(leads);
      const totalInterviews = safeArrayLength(interviews);
      const totalEmployees = safeArrayLength(employees);
      
      const passedInterviews = safeArrayFilter(interviews, i => (i.result === 'passed') || (i.status === 'suitable')).length;
      const activeEmployees = safeArrayFilter(employees, e => e.status === 'active').length;
      const completedOnboarding = safeArrayFilter(onboarding, o => o.status === 'completed').length;

      const conversionRate = totalLeads > 0 ? Number(((completedOnboarding / totalLeads) * 100).toFixed(1)) : 0;
      const interviewPassRate = totalInterviews > 0 ? Number(((passedInterviews / totalInterviews) * 100).toFixed(1)) : 0;
      const retentionRate = totalEmployees > 0 ? ((activeEmployees / totalEmployees) * 100).toFixed(1) : 0;

      setMetrics({
        totalLeads,
        totalInterviews,
        totalEmployees,
        conversionRate: Number(conversionRate),
        interviewPassRate: Number(interviewPassRate),
        retentionRate,
        turnoverRate: 0
      });

      // 计算今日指标
      const today = dayjs().startOf('day');
      const todayLeads = safeArrayFilter(leads, l => dayjs(l.created_at).isSame(today, 'day')).length;
      const todayInterviews = safeArrayFilter(interviews, i => dayjs(i.interview_date).isSame(today, 'day')).length;
      const todayFollowUps = safeArrayFilter(followUps, f => dayjs(f.created_at).isSame(today, 'day')).length;
      const todayOnboarding = safeArrayFilter(onboarding, o => dayjs(o.created_at).isSame(today, 'day')).length;
      const pendingTasks = todayFollowUps + todayInterviews + todayOnboarding;

      setTodayMetrics({
        todayLeads,
        todayInterviews,
        todayFollowUps,
        todayOnboarding,
        pendingTasks
      });

      setLoading(false);
    } catch (error) {
      console.error('获取仪表盘数据失败:', error);
      setData({ leads: [], interviews: [], employees: [], onboarding: [], followUps: [], users: [] });
      setMetrics({
        totalLeads: 0,
        totalInterviews: 0,
        totalEmployees: 0,
        conversionRate: 0,
        interviewPassRate: 0,
        retentionRate: 0,
        turnoverRate: 0
      });
      setTodayMetrics({
        todayLeads: 0,
        todayInterviews: 0,
        todayFollowUps: 0,
        todayOnboarding: 0,
        pendingTasks: 0
      });
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '10px 16px' }}>
      <div style={{ marginTop: '4px' }}>
        {/* 工作概览 */}
        <Card 
          title="工作概览" 
          style={{ marginBottom: '24px' }}
          extra={
            <Button 
              type="primary" 
              icon={<ReloadOutlined />} 
              onClick={() => { fetchDashboardData(); }}
              loading={loading}
              size="small"
            />
          }
        >
          {/* 线索相关指标 */}
          <Card size="small" style={{ marginBottom: '16px' }} title="线索管理" extra={<UserOutlined style={{ color: '#1890ff' }} />}>
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="今日线索"
                  value={todayMetrics.todayLeads}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="昨日线索"
                  value={(() => {
                    const yesterday = dayjs().subtract(1, 'day').startOf('day');
                    return safeArrayFilter(data.leads, l => dayjs(l.created_at).isSame(yesterday, 'day')).length;
                  })()}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本周线索"
                  value={(() => {
                    const weekStart = dayjs().startOf('week');
                    return safeArrayFilter(data.leads, l => dayjs(l.created_at).isAfter(weekStart)).length;
                  })()}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本月线索"
                  value={(() => {
                    const monthStart = dayjs().startOf('month');
                    return safeArrayFilter(data.leads, l => dayjs(l.created_at).isAfter(monthStart)).length;
                  })()}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="我的线索"
                  value={(() => {
                    return safeArrayFilter(data.leads, l => l.assigned_to === 'current_user').length;
                  })()}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="待跟进"
                  value={todayMetrics.todayFollowUps}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
            </Row>
          </Card>
          
          {/* 面试与入职相关指标 */}
          <Card size="small" style={{ marginBottom: '16px' }} title="面试与入职" extra={<CalendarOutlined style={{ color: '#52c41a' }} />}>
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="今日面试"
                  value={todayMetrics.todayInterviews}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本周面试"
                  value={(() => {
                    const weekStart = dayjs().startOf('week');
                    return safeArrayFilter(data.interviews, i => dayjs(i.interview_date).isAfter(weekStart)).length;
                  })()}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本月面试"
                  value={(() => {
                    const monthStart = dayjs().startOf('month');
                    return safeArrayFilter(data.interviews, i => dayjs(i.interview_date).isAfter(monthStart)).length;
                  })()}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="今日入职"
                  value={todayMetrics.todayOnboarding}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本月入职"
                  value={(() => {
                    const monthStart = dayjs().startOf('month');
                    return safeArrayFilter(data.onboarding, o => dayjs(o.created_at).isAfter(monthStart)).length;
                  })()}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="面试通过率"
                  value={metrics.interviewPassRate}
                  suffix="%"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
            </Row>
          </Card>
          
          {/* 员工与任务相关指标 */}
          <Card size="small" title="员工与任务" extra={<TeamOutlined style={{ color: '#52c41a' }} />}>
            <Row gutter={16}>
              <Col span={4}>
                <Statistic
                  title="在职员工"
                  value={safeArrayFilter(data.employees, e => e.status === 'active').length}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本月离职"
                  value={(() => {
                    const monthStart = dayjs().startOf('month');
                    return safeArrayFilter(data.employees, e => e.status === 'inactive' && dayjs(e.updated_at).isAfter(monthStart)).length;
                  })()}
                  valueStyle={{ color: '#f5222d' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="待办任务"
                  value={todayMetrics.pendingTasks}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="跟进任务"
                  value={todayMetrics.todayFollowUps}
                  valueStyle={{ color: '#13c2c2' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="转化率"
                  value={metrics.conversionRate}
                  suffix="%"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic
                  title="本月用工成本"
                  value="预估"
                  valueStyle={{ color: '#722ed1' }}
                  prefix={<DollarOutlined />}
                  suffix={
                    <Button 
                      type="link" 
                      size="small"
                      onClick={() => setCostEstimatorVisible(true)}
                    >
                      查看
                    </Button>
                  }
                />
              </Col>
            </Row>
          </Card>
        </Card>
      </div>

      {/* 本月用工成本预估器 */}
      <MonthlyLaborCostEstimator
        visible={costEstimatorVisible}
        onCancel={() => setCostEstimatorVisible(false)}
      />
    </div>
  );
};

export default Dashboard;